import { useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Send,
  X,
  Paperclip,
  ChevronDown,
  FileText,
  Clock,
  Save,
  MoreHorizontal,
  Eye,
  Trash2,
  Loader2,
} from 'lucide-react';
import { EmailTemplates } from './EmailTemplates';
import type { Contact } from '@/types/contacts';

interface EmailComposerProps {
  contact: Contact;
  onClose: () => void;
  onSent: () => void;
  initialTemplate?: { subject: string; body: string };
}

interface Attachment {
  id: string;
  name: string;
  size: number;
  type: string;
}

export function EmailComposer({ contact, onClose, onSent, initialTemplate }: EmailComposerProps) {
  const [subject, setSubject] = useState(initialTemplate?.subject || '');
  const [body, setBody] = useState(initialTemplate?.body || '');
  const [to, setTo] = useState(contact.email);
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [showCcBcc, setShowCcBcc] = useState(false);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [trackOpens, setTrackOpens] = useState(true);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Template variable replacement
  const processTemplate = (template: { subject: string; body: string }) => {
    const variables: Record<string, string> = {
      firstName: contact.name.split(' ')[0],
      lastName: contact.name.split(' ').slice(1).join(' '),
      fullName: contact.name,
      company: contact.company || 'your company',
      title: contact.title || '',
      email: contact.email,
      senderName: 'Your Name', // TODO: Get from user context
      senderCompany: 'Your Company',
      senderTitle: '',
      senderPhone: '',
    };

    let processedSubject = template.subject;
    let processedBody = template.body;

    Object.entries(variables).forEach(([key, value]) => {
      const regex = new RegExp(`{{${key}}}`, 'g');
      processedSubject = processedSubject.replace(regex, value);
      processedBody = processedBody.replace(regex, value);
    });

    setSubject(processedSubject);
    setBody(processedBody);
    setShowTemplates(false);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const newAttachments: Attachment[] = Array.from(files).map((file) => ({
      id: Math.random().toString(36).substr(2, 9),
      name: file.name,
      size: file.size,
      type: file.type,
    }));

    setAttachments((prev) => [...prev, ...newAttachments]);
  };

  const removeAttachment = (id: string) => {
    setAttachments((prev) => prev.filter((a) => a.id !== id));
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleSend = async () => {
    if (!subject.trim() || !body.trim() || !to.trim()) return;

    setIsSending(true);
    try {
      const response = await fetch('/api/crm/communications/emails', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contactId: contact.id,
          subject,
          body,
          toEmail: to,
          cc: cc || undefined,
          bcc: bcc || undefined,
          status: 'sent',
          attachments: attachments.map((a) => ({
            filename: a.name,
            size: a.size,
            mimeType: a.type,
          })),
        }),
      });

      if (response.ok) {
        onSent();
      } else {
        const error = await response.json();
        console.error('Failed to send email:', error);
      }
    } catch (error) {
      console.error('Send email error:', error);
    } finally {
      setIsSending(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!subject.trim() && !body.trim()) return;

    setIsSaving(true);
    try {
      const response = await fetch('/api/crm/communications/emails', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contactId: contact.id,
          subject: subject || '(No subject)',
          body,
          toEmail: to,
          cc: cc || undefined,
          bcc: bcc || undefined,
          status: 'draft',
        }),
      });

      if (response.ok) {
        onClose();
      }
    } catch (error) {
      console.error('Save draft error:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const insertVariable = (variable: string) => {
    const textarea = document.getElementById('email-body') as HTMLTextAreaElement;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const newBody = body.substring(0, start) + `{{${variable}}}` + body.substring(end);
    setBody(newBody);

    // Restore focus and cursor position
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + variable.length + 4, start + variable.length + 4);
    }, 0);
  };

  const variables = [
    { key: 'firstName', label: 'First Name' },
    { key: 'lastName', label: 'Last Name' },
    { key: 'company', label: 'Company' },
    { key: 'title', label: 'Title' },
    { key: 'senderName', label: 'Your Name' },
    { key: 'senderCompany', label: 'Your Company' },
  ];

  return (
    <>
      <Dialog open onOpenChange={onClose}>
        <DialogContent className="max-w-3xl max-h-[90vh] p-0 overflow-hidden">
          <DialogHeader className="p-6 pb-4 border-b">
            <div className="flex items-center justify-between">
              <DialogTitle className="flex items-center gap-2">
                <Send className="h-5 w-5 text-[#2B7FFF]" />
                Compose Email
              </DialogTitle>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowTemplates(true)}
                  className="text-[#2B7FFF]"
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Templates
                </Button>
              </div>
            </div>
          </DialogHeader>

          <div className="p-6 space-y-4">
            {/* To Field */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground w-12">To:</span>
                <Input
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                  placeholder="recipient@example.com"
                  className="flex-1"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowCcBcc(!showCcBcc)}
                  className="text-muted-foreground"
                >
                  Cc/Bcc
                </Button>
              </div>

              <AnimatePresence>
                {showCcBcc && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="space-y-2"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground w-12">Cc:</span>
                      <Input
                        value={cc}
                        onChange={(e) => setCc(e.target.value)}
                        placeholder="cc@example.com"
                        className="flex-1"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground w-12">Bcc:</span>
                      <Input
                        value={bcc}
                        onChange={(e) => setBcc(e.target.value)}
                        placeholder="bcc@example.com"
                        className="flex-1"
                      />
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Subject */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground w-12">Subject:</span>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Enter subject..."
                className="flex-1"
              />
            </div>

            {/* Variables Toolbar */}
            <div className="flex items-center gap-2 py-2 border-y">
              <span className="text-xs text-muted-foreground">Insert:</span>
              <div className="flex items-center gap-1 flex-wrap">
                {variables.map((v) => (
                  <button
                    key={v.key}
                    onClick={() => insertVariable(v.key)}
                    className="text-xs px-2 py-1 rounded bg-[#2B7FFF]/10 text-[#2B7FFF] hover:bg-[#2B7FFF]/20 transition-colors"
                  >
                    {v.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Body */}
            <Textarea
              id="email-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Write your message here..."
              className="min-h-[250px] resize-none"
            />

            {/* Attachments */}
            {attachments.length > 0 && (
              <div className="space-y-2">
                <span className="text-sm font-medium">Attachments</span>
                <div className="flex flex-wrap gap-2">
                  {attachments.map((attachment) => (
                    <Badge
                      key={attachment.id}
                      variant="secondary"
                      className="flex items-center gap-2 pr-1"
                    >
                      <Paperclip className="h-3 w-3" />
                      <span className="max-w-[150px] truncate">{attachment.name}</span>
                      <span className="text-xs text-muted-foreground">
                        ({formatFileSize(attachment.size)})
                      </span>
                      <button
                        onClick={() => removeAttachment(attachment.id)}
                        className="ml-1 p-0.5 hover:bg-muted rounded"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Options */}
            <div className="flex items-center justify-between pt-2">
              <div className="flex items-center gap-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Paperclip className="h-4 w-4 mr-2" />
                  Attach
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  onChange={handleFileSelect}
                  className="hidden"
                />

                <div className="flex items-center gap-2">
                  <Switch
                    id="track-opens"
                    checked={trackOpens}
                    onCheckedChange={setTrackOpens}
                  />
                  <Label htmlFor="track-opens" className="text-sm cursor-pointer">
                    <Eye className="h-3.5 w-3.5 inline mr-1" />
                    Track opens
                  </Label>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  onClick={handleSaveDraft}
                  disabled={isSaving || (!subject.trim() && !body.trim())}
                >
                  {isSaving ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  Save Draft
                </Button>
                <Button
                  onClick={handleSend}
                  disabled={isSending || !subject.trim() || !body.trim() || !to.trim()}
                  className="bg-[#2B7FFF] hover:bg-[#1A5FCC]"
                >
                  {isSending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4 mr-2" />
                  )}
                  Send Email
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {showTemplates && (
        <EmailTemplates
          onSelect={processTemplate}
          onClose={() => setShowTemplates(false)}
        />
      )}
    </>
  );
}

export default EmailComposer;
